import React, { useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { getPlacementSummary, PlacementBucket } from '../data/player_insights';
import { PLACE_META } from '../charts/PlacementDoughnut';
import { DS, Meter, Overline, Panel, numericSx } from '../shared/Dashboard';

const PlaceTile: React.FC<{ bucket: PlacementBucket }> = ({ bucket }) => {
    const { label, color } = PLACE_META[bucket.place];
    return (
        <Box sx={{
            flex: 1,
            p: 1.75,
            borderRadius: '12px',
            background: alpha(color, 0.08),
            border: `1px solid ${alpha(color, 0.24)}`,
        }}>
            <Overline color={color}>{label}</Overline>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: DS.text, lineHeight: 1.2, ...numericSx }}>
                {bucket.percent}%
            </Typography>
            <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{bucket.count} rounds</Typography>
        </Box>
    );
};

const PlacePercentages = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData } = context || {};

    const summary = useMemo(() => getPlacementSummary(historyData ?? []), [historyData]);

    if (isEmpty(historyData)) return null;

    const maxPercent = Math.max(...summary.buckets.map(b => b.percent), 1);

    return (
        <Panel
            title="Finishing positions"
            subtitle={<>Average finish <strong style={{ color: DS.text }}>{summary.averagePlace || '—'}</strong> · {summary.winPercent}% wins</>}
            grid={{ xs: 12, md: 6 }}
            delay={0.1}
        >
            <Box sx={{ display: 'flex', gap: 1 }}>
                {summary.buckets.slice(0, 3).map(b => <PlaceTile key={b.place} bucket={b} />)}
            </Box>

            <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {summary.buckets.map(b => {
                    const { label, color } = PLACE_META[b.place];
                    return (
                        <Box key={b.place} sx={{ display: 'grid', gridTemplateColumns: '96px 1fr 72px', alignItems: 'center', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text, lineHeight: 1.2 }}>{label}</Typography>
                                {b.tied > 0 && (
                                    <Typography sx={{ fontSize: '11px', color: DS.textFaint, ...numericSx }}>incl. {b.tied} tied</Typography>
                                )}
                            </Box>
                            {/* Scaled to the most common finish so small differences stay visible */}
                            <Meter value={b.percent / maxPercent} color={color} height={8} />
                            <Typography sx={{ fontSize: '13px', color: DS.textMuted, textAlign: 'right', ...numericSx }}>
                                <strong style={{ color: DS.text }}>{b.percent}%</strong> · {b.count}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Panel>
    );
};

export default PlacePercentages;
